import React, { useCallback, useMemo, useState } from "react";

import { mdiCloudDownloadOutline } from "@mdi/js";
import Icon from "@mdi/react";
import { useQuery } from "@tanstack/react-query";
import moment from "moment";
import { Calendar, momentLocalizer } from "react-big-calendar";
import { useTranslation } from "react-i18next";
import { useHistory, useParams } from "react-router-dom";

import useMediaColorByFormat from "@/src/hooks/useMediaColorByFormat";

import CalendarDateCellWrapper from "./CalendarDateCellWrapper";
import CalendarDayColumnWrapper from "./CalendarDayColumnWrapper";
import CalendarExportModal from "./CalendarExportModal";
import { MonthHeader, TimeGutterHeader, WeekHeader } from "./CalendarHeader";
import CalendarOverview from "./CalendarOverview";
import ScheduleButton from "./CalendarScheduleButton";
import CalendarSchedulingModal from "./CalendarSchedulingModal";
import CalendarWeekEvent from "./CalendarWeekEvent";
import CalendarWeekEventSidePanel from "./CalendarWeekEventSidePanel";
import FastAPI from "../../../api/FastAPI";
import { API_DATE_FORMAT } from "../../../constants/dateFormat";
import useFAST from "../../../contexts/FASTContext";
import "../../../css/admin/calendar.scss";
import useGetSchedulesQuery from "../../../hooks/queries/useGetSchedulesQuery";
import useCalendarUrlParams from "../../../hooks/useCalendarUrlParams";
import useFASTLayout from "../../../hooks/useFASTLayout";
import useFASTScheduleWebSocket from "../../../hooks/useFASTScheduleWebSocket";
import { getCalendarItems } from "../../../utils/calendarClusters";
import {
  convertFirstDayOfWeekStringToNumber,
  getItemRendererStyle,
  getTimeboundsQueryString,
} from "../../../utils/fast";
import {
  isCalendarClusterEvent,
  isCalendarView,
} from "../../../utils/typePredicate";
import Block from "../../block/Block";
import Button from "../../button/Button";
import { FAST_CHANNELS_ROUTE } from "../../routes/routes";
import Tooltip from "../../tooltip/Tooltip";
import AbilityContainer from "../../utils/abilities/AbilityContainer";
import Spinner from "../../utils/Spinner";
import EditModeBox from "../editMode/EditModeBox";

import type { Truncated } from "okast-timeline";
import type { CSSProperties, JSX } from "react";
import type {
  Components,
  DateCellWrapperProps,
  DateLocalizer,
  Event,
  Formats,
  HeaderProps,
  View,
} from "react-big-calendar";

import type { MediaFormat } from "../../../constants/media";
import type { FASTChannelRoutesParams } from "../../routes/FASTChannelRoutes";
import type { InterstitialAdbreaks } from "../timeline/timeline.types";

export interface CalendarEvent extends Event {
  resource: {
    uuid: string;
    label: string;
    startTime: string;
    endTime: string;
    duration: number;
    picture: string | null;
    format: MediaFormat | null;
    scheduleId: string;
    truncated: Truncated;
    interstitialAdbreaks?: InterstitialAdbreaks;
  };
}

const CALENDAR_HEIGHTS = {
  month: 500,
  week: "unset",
} as const;

interface CalendarParams {
  formats: Formats;
  components: Components<CalendarEvent>;
}

function CustomCalendar(): JSX.Element {
  const { t, i18n } = useTranslation("editor-fast-channel");
  const history = useHistory();
  const { channelId } = useParams<FASTChannelRoutesParams>();
  const { state } = useFAST();
  const [showScheduleModal, setShowScheduleModal] = useState<boolean>(false);
  const [showExportModal, setShowExportModal] = useState<boolean>(false);
  const [isOpenSidePanel, setIsOpenSidePanel] = useState(false);
  const [selectedEvent, setSelectedEvent] = useState<CalendarEvent | null>(
    null,
  );

  const { currentDate, currentView, updateCalendarUrlParams } =
    useCalendarUrlParams();

  const { releaseSchedule } = useFASTScheduleWebSocket();

  const firstDayOfWeek = convertFirstDayOfWeekStringToNumber(
    state.channelData?.first_day_of_week,
  );

  moment.updateLocale(i18n.language, {
    week: {
      dow: firstDayOfWeek,
    },
  });

  const globalLocalizer = momentLocalizer(moment);

  useFASTLayout({
    title: t("calendar"),
    button: (
      <div className="flex items-center gap-[0.78125rem]">
        <AbilityContainer abilityList={["fastChannelsWrite"]}>
          <EditModeBox />
        </AbilityContainer>
        <Button
          className="my-1.25"
          onClick={(): void => setShowExportModal(true)}
          size="md"
          type="button"
        >
          <Icon path={mdiCloudDownloadOutline} size={0.9375} />
          {t("exportChannelReport")}
        </Button>
        <AbilityContainer abilityList={["fastChannelsWrite"]}>
          <ScheduleButton setShowScheduleModal={setShowScheduleModal} />
        </AbilityContainer>
      </div>
    ),
  });

  const { timelineScheduleSelected } = state;

  const { data: schedules, isLoading: isLoadingSchedules } =
    useGetSchedulesQuery({ channelId });

  const scheduleList = schedules?.data ?? [];

  const scheduleSelected = scheduleList.find(
    (schedule) => schedule.uuid === timelineScheduleSelected,
  );

  const isMonthView = currentView === "month";
  const isWeekView = currentView === "week";

  const onNavigate = useCallback(
    (newDate: Date) => {
      updateCalendarUrlParams({ date: newDate });
    },
    [updateCalendarUrlParams],
  );

  const startDate = moment(currentDate)
    .startOf("month")
    .subtract(7, "day")
    .format(API_DATE_FORMAT);

  const endDate = moment(currentDate)
    .endOf("month")
    .add(7, "day")
    .format(API_DATE_FORMAT);

  const getCalendarFillingQuery = FastAPI.generateQuery({
    fn: FastAPI.getCalendarFilling,
    params: [
      {
        channelId,
        scheduleId: timelineScheduleSelected ?? "",
        periodStart: startDate,
        periodEnd: endDate,
      },
    ],
  });

  const {
    data: calendarFilling,
    isLoading: isLoadingCalendarFilling,
    isFetching: isFetchingCalendarFilling,
  } = useQuery({
    ...getCalendarFillingQuery,
    enabled: Boolean(channelId && timelineScheduleSelected && isMonthView),
    keepPreviousData: true,
  });

  const getCalendarFillingWeekQuery = FastAPI.generateQuery({
    fn: FastAPI.getCalendarFillingWeek,
    params: [
      {
        channelId,
        week: moment(currentDate).startOf("week").format(API_DATE_FORMAT),
      },
    ],
  });

  const { data: calendarFillingWeek, isLoading: isLoadingCalendarFillingWeek } =
    useQuery({
      ...getCalendarFillingWeekQuery,
      enabled: Boolean(channelId && isWeekView),
    });

  const eventWeekList: CalendarEvent[] = getCalendarItems(calendarFillingWeek);

  const handleClick = (date: Date): void => {
    const timeboundsQuery = getTimeboundsQueryString(date);
    history.push(
      `${FAST_CHANNELS_ROUTE}/${channelId}/scheduling${timeboundsQuery}`,
    );
  };

  const { formats, components } = useMemo(
    () =>
      ({
        formats: {
          weekdayFormat: (
            date: Date,
            culture?: string,
            localizer?: DateLocalizer,
          ) => localizer?.format(date, "dddd", culture) ?? "",
        },
        components: {
          timeGutterHeader: TimeGutterHeader,
          dayColumnWrapper: CalendarDayColumnWrapper,
          dateCellWrapper: (
            dateCellWrapperProps: DateCellWrapperProps,
          ): JSX.Element => {
            const { value } = dateCellWrapperProps;
            const currentDay = moment(value).format("YYYY-MM-DD");
            return (
              <CalendarDateCellWrapper
                dayFilling={calendarFilling?.data?.[currentDay]}
                onClick={handleClick}
                range={dateCellWrapperProps.range}
                value={value}
              >
                {dateCellWrapperProps.children}
              </CalendarDateCellWrapper>
            );
          },
          toolbar: () => null,
          month: {
            header: MonthHeader,
            dateHeader: () => null,
          },
          week: {
            header: (headerProps: HeaderProps): JSX.Element => (
              <WeekHeader
                {...headerProps} // eslint-disable-line react/jsx-props-no-spreading
                onClick={handleClick}
              />
            ),
            event: CalendarWeekEvent,
          },
        },
      }) as unknown as CalendarParams,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [currentDate, calendarFilling?.data],
  );

  const isLoading = useMemo(() => {
    if (isMonthView) {
      return (
        isLoadingSchedules ||
        isLoadingCalendarFilling ||
        isFetchingCalendarFilling
      );
    }

    return isLoadingSchedules || isLoadingCalendarFillingWeek;
  }, [
    isFetchingCalendarFilling,
    isLoadingCalendarFilling,
    isLoadingCalendarFillingWeek,
    isLoadingSchedules,
    isMonthView,
  ]);

  const handleModalClose = useCallback((): void => {
    setShowScheduleModal(false);
    releaseSchedule();
  }, [setShowScheduleModal, releaseSchedule]);

  const handleChangeView = (view: View): void => {
    if (!isCalendarView(view)) return;

    updateCalendarUrlParams({ view });
  };

  const handleChangeDate = (date: Date): void => {
    updateCalendarUrlParams({ date });
  };

  const getMediaColor = useMediaColorByFormat();

  const eventPropGetter = useCallback(
    (
      event: CalendarEvent,
    ): { className?: string | undefined; style?: CSSProperties } => {
      const itemColor = getMediaColor(event.resource.format);

      const style = getItemRendererStyle({
        color: itemColor,
        isVerticalTimeline: true,
        truncated: event.resource.truncated,
      });

      return {
        style,
      };
    },
    [getMediaColor],
  );

  const handleSelectEvent = (event: CalendarEvent): void => {
    const isCluster = selectedEvent && isCalendarClusterEvent(selectedEvent);

    if (isCluster) return;

    setSelectedEvent(event);
    setIsOpenSidePanel(true);
  };

  const handleClose = (): void => {
    setIsOpenSidePanel(false);
  };

  return (
    <div className="flex w-full flex-col gap-[0.78125rem]">
      <Block title={t("calendarTitle")}>
        <CalendarOverview
          currentDate={new Date(currentDate)}
          currentView={currentView}
          handleChangeDate={handleChangeDate}
          isLoading={isLoading}
          onChangeView={handleChangeView}
          scheduleSelected={scheduleSelected}
        />
      </Block>
      <Block>
        {isLoading ? (
          <div
            className="flex w-full items-center justify-center"
            style={{
              height: CALENDAR_HEIGHTS[currentView],
              minHeight: 500,
            }}
          >
            <Spinner />
          </div>
        ) : (
          <Calendar
            key={i18n.language}
            components={components}
            date={new Date(currentDate)}
            dayLayoutAlgorithm="no-overlap"
            endAccessor="end"
            eventPropGetter={eventPropGetter}
            events={isMonthView ? [] : eventWeekList}
            formats={formats}
            localizer={globalLocalizer}
            onNavigate={onNavigate}
            onSelectEvent={handleSelectEvent}
            onView={handleChangeView}
            startAccessor="start"
            style={{ height: CALENDAR_HEIGHTS[currentView] }}
            toolbar={false}
            tooltipAccessor={(): string => ""}
            view={currentView}
            views={["month", "week"]}
          />
        )}
        <Tooltip id="calendar-date-cell" />
      </Block>
      <CalendarSchedulingModal
        isOpen={showScheduleModal}
        onClose={handleModalClose}
      />
      <CalendarExportModal
        isOpen={showExportModal}
        onClose={(): void => setShowExportModal(false)}
      />
      <CalendarWeekEventSidePanel
        event={selectedEvent}
        isOpen={isOpenSidePanel}
        onClose={handleClose}
      />
    </div>
  );
}

export default CustomCalendar;
